import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  TrendingUp, 
  Target, 
  Mail, 
  Share2,
  Star,
  Crown,
  Zap,
  BarChart3,
  Gift,
  Bell,
  Eye,
  MapPin,
  Users,
  Calendar,
  CheckCircle,
  XCircle
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Marketing Tools Configuration
const MARKETING_TOOLS = {
  // Plan Gratuit (20% commission)
  free: {
    planName: "Plan Gratuit",
    commission: "20%",
    tools: [
      {
        id: "basic_stats",
        name: "Statistiques de base",
        description: "Vues de profil et nombre de réservations",
        icon: BarChart3,
        category: "analytics"
      },
      {
        id: "profile_badge",
        name: "Badge Chef Vérifié",
        description: "Badge de base sur votre profil",
        icon: CheckCircle,
        category: "visibility"
      }
    ]
  },
  
  // Plan Standard (10% commission)
  standard: {
    planName: "Plan Standard",
    commission: "10%",
    tools: [
      // Hérite des outils gratuits
      {
        id: "advanced_stats",
        name: "Analytics Avancés",
        description: "Taux de conversion, sources de trafic, mots-clés",
        icon: TrendingUp,
        category: "analytics"
      },
      {
        id: "certified_badge",
        name: "Badge Chef Certifié",
        description: "Badge premium doré sur votre profil",
        icon: Star,
        category: "visibility"
      },
      {
        id: "search_boost",
        name: "Boost dans les Recherches",
        description: "Apparition prioritaire dans les résultats",
        icon: Eye,
        category: "visibility"
      },
      {
        id: "discount_codes",
        name: "Codes de Réduction",
        description: "Créez des codes promo personnalisés",
        icon: Gift,
        category: "promotion"
      },
      {
        id: "client_notifications",
        name: "Notifications Clients",
        description: "Rappels automatiques aux clients intéressés",
        icon: Bell,
        category: "automation"
      }
    ]
  },
  
  // Plan Premium (0% commission)
  premium: {
    planName: "Plan Premium",
    commission: "0%",
    tools: [
      // Hérite de tous les outils précédents
      {
        id: "homepage_spotlight",
        name: "Mise en Avant Accueil",
        description: "Votre atelier en vedette sur la page d'accueil",
        icon: Crown,
        category: "visibility"
      },
      {
        id: "premium_badge",
        name: "Badge Chef Premium",
        description: "Badge exclusif violet avec couronne",
        icon: Crown,
        category: "visibility"
      },
      {
        id: "newsletter_feature",
        name: "Newsletter Dédiée",
        description: "Votre atelier dans la newsletter hebdomadaire",
        icon: Mail,
        category: "promotion"
      },
      {
        id: "referral_program",
        name: "Programme de Parrainage",
        description: "Liens de parrainage avec commissions",
        icon: Share2,
        category: "promotion"
      },
      {
        id: "geo_targeting",
        name: "Ciblage Géographique",
        description: "Campagnes ciblées par ville/région",
        icon: MapPin,
        category: "targeting"
      },
      {
        id: "social_media",
        name: "Promotion Réseaux Sociaux",
        description: "Spotlights mensuels sur nos réseaux sociaux",
        icon: Zap,
        category: "promotion"
      },
      {
        id: "personal_marketing",
        name: "Support Marketing Dédié",
        description: "Conseils personnalisés pour optimiser vos ateliers",
        icon: Target,
        category: "support"
      }
    ]
  }
};

const MarketingToolCard = ({ tool, isAvailable, isActive, onToggle }) => {
  const Icon = tool.icon;
  const categoryColors = {
    analytics: "bg-blue-100 text-blue-800",
    visibility: "bg-orange-100 text-orange-800", 
    promotion: "bg-green-100 text-green-800",
    automation: "bg-purple-100 text-purple-800",
    targeting: "bg-red-100 text-red-800",
    support: "bg-yellow-100 text-yellow-800"
  };

  return (
    <Card className={`${isAvailable ? '' : 'opacity-50'} ${isActive ? 'ring-2 ring-orange-500' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isAvailable ? 'bg-orange-100' : 'bg-gray-100'}`}>
              <Icon className={`w-5 h-5 ${isAvailable ? 'text-orange-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <CardTitle className="text-sm">{tool.name}</CardTitle>
              <Badge className={`text-xs ${categoryColors[tool.category] || 'bg-gray-100 text-gray-800'}`}>
                {tool.category}
              </Badge>
            </div>
          </div>
          {isAvailable && (
            <Button
              size="sm"
              variant={isActive ? "default" : "outline"}
              onClick={() => onToggle(tool.id)}
              className="text-xs"
            >
              {isActive ? "Actif" : "Activer"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600">{tool.description}</p>
        {!isAvailable && (
          <p className="text-xs text-orange-600 mt-2 font-medium">
            📈 Disponible avec un plan supérieur
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const MarketingStats = ({ userPlan, activeTools }) => {
  const [stats, setStats] = useState({
    profileViews: Math.floor(Math.random() * 500) + 100,
    workshopViews: Math.floor(Math.random() * 1200) + 300,
    conversionRate: (Math.random() * 15 + 5).toFixed(1),
    searchRanking: Math.floor(Math.random() * 10) + 1,
    socialReach: Math.floor(Math.random() * 5000) + 1000
  });

  useEffect(() => {
    // Simulate real-time stats updates
    const interval = setInterval(() => {
      if (activeTools.includes('homepage_spotlight')) {
        setStats(prev => ({
          ...prev,
          profileViews: prev.profileViews + Math.floor(Math.random() * 10),
          socialReach: prev.socialReach + Math.floor(Math.random() * 50)
        }));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [activeTools]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="pt-4">
          <div className="text-2xl font-bold text-orange-600">{stats.profileViews}</div>
          <p className="text-sm text-gray-600">Vues de profil</p>
          {activeTools.includes('search_boost') && (
            <p className="text-xs text-green-600">+25% boost actif</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="text-2xl font-bold text-blue-600">{stats.workshopViews}</div>
          <p className="text-sm text-gray-600">Vues d'ateliers</p>
          {activeTools.includes('homepage_spotlight') && (
            <p className="text-xs text-green-600">Spotlight actif</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="text-2xl font-bold text-green-600">{stats.conversionRate}%</div>
          <p className="text-sm text-gray-600">Taux de conversion</p>
          {activeTools.includes('discount_codes') && (
            <p className="text-xs text-green-600">Codes promo actifs</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="text-2xl font-bold text-purple-600">#{stats.searchRanking}</div>
          <p className="text-sm text-gray-600">Rang moyen recherche</p>
          {userPlan === 'premium' && (
            <p className="text-xs text-green-600">Position premium</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const PromoCodeManager = ({ isAvailable }) => {
  const [promoCodes, setPromoCodes] = useState([
    { id: 1, code: "CUISINE10", discount: 10, uses: 5, active: true },
    { id: 2, code: "BIENVENUE", discount: 15, uses: 12, active: true }
  ]);
  const [newCode, setNewCode] = useState({ code: "", discount: "" });

  const handleCreateCode = () => {
    if (newCode.code && newCode.discount) {
      const code = {
        id: Date.now(),
        code: newCode.code.toUpperCase(),
        discount: parseInt(newCode.discount),
        uses: 0,
        active: true
      };
      setPromoCodes([...promoCodes, code]);
      setNewCode({ code: "", discount: "" });
    }
  };

  if (!isAvailable) {
    return (
      <Card className="opacity-50">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Gift className="w-5 h-5 mr-2" />
            Codes de Réduction
          </CardTitle>
          <CardDescription>Disponible avec le Plan Standard ou Premium</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Gift className="w-5 h-5 mr-2" />
          Codes de Réduction
        </CardTitle>
        <CardDescription>Créez des codes promo pour attirer plus de clients</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex space-x-2">
            <Input
              placeholder="Code (ex: CUISINE10)"
              value={newCode.code}
              onChange={(e) => setNewCode({...newCode, code: e.target.value})}
              className="flex-1"
            />
            <Input
              type="number"
              placeholder="% remise"
              value={newCode.discount}
              onChange={(e) => setNewCode({...newCode, discount: e.target.value})}
              className="w-24"
            />
            <Button onClick={handleCreateCode} className="bg-orange-600 hover:bg-orange-700">
              Créer
            </Button>
          </div>

          <div className="space-y-2">
            {promoCodes.map((code) => (
              <div key={code.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <span className="font-mono font-semibold">{code.code}</span>
                  <span className="ml-2 text-green-600">-{code.discount}%</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{code.uses} utilisations</span>
                  <Badge variant={code.active ? "default" : "secondary"}>
                    {code.active ? "Actif" : "Inactif"}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MarketingTools = ({ user, currentSubscription }) => {
  const [activeTools, setActiveTools] = useState([
    "basic_stats", 
    "profile_badge",
    ...(currentSubscription?.plan_name?.includes('Standard') ? ['search_boost', 'certified_badge'] : []),
    ...(currentSubscription?.plan_name?.includes('Premium') ? ['homepage_spotlight', 'premium_badge'] : [])
  ]);

  const getUserPlan = () => {
    if (!currentSubscription || currentSubscription.plan_name?.includes('Gratuit')) return 'free';
    if (currentSubscription.plan_name?.includes('Standard')) return 'standard';
    if (currentSubscription.plan_name?.includes('Premium')) return 'premium';
    return 'free';
  };

  const userPlan = getUserPlan();
  const availableTools = getAllAvailableTools(userPlan);

  const handleToggleTool = (toolId) => {
    if (activeTools.includes(toolId)) {
      setActiveTools(activeTools.filter(id => id !== toolId));
    } else {
      setActiveTools([...activeTools, toolId]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Outils Marketing</h2>
          <p className="text-gray-600">
            Plan actuel: <span className="font-semibold text-orange-600">
              {MARKETING_TOOLS[userPlan].planName}
            </span> - Commission: {MARKETING_TOOLS[userPlan].commission}
          </p>
        </div>
      </div>

      <MarketingStats userPlan={userPlan} activeTools={activeTools} />

      <Tabs defaultValue="tools" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="tools">Mes Outils</TabsTrigger>
          <TabsTrigger value="promo">Codes Promo</TabsTrigger>
          <TabsTrigger value="upgrade">Upgrader</TabsTrigger>
        </TabsList>

        <TabsContent value="tools" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {availableTools.map((tool) => (
              <MarketingToolCard
                key={tool.id}
                tool={tool}
                isAvailable={true}
                isActive={activeTools.includes(tool.id)}
                onToggle={handleToggleTool}
              />
            ))}
            
            {/* Show locked tools */}
            {userPlan !== 'premium' && (
              <>
                {MARKETING_TOOLS.premium.tools
                  .filter(tool => !availableTools.find(t => t.id === tool.id))
                  .slice(0, 2)
                  .map((tool) => (
                    <MarketingToolCard
                      key={tool.id}
                      tool={tool}
                      isAvailable={false}
                      isActive={false}
                      onToggle={() => {}}
                    />
                  ))}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="promo" className="mt-6">
          <PromoCodeManager 
            isAvailable={userPlan === 'standard' || userPlan === 'premium'} 
          />
        </TabsContent>

        <TabsContent value="upgrade" className="mt-6">
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(MARKETING_TOOLS).map(([planKey, planData]) => (
              <Card key={planKey} className={`${userPlan === planKey ? 'ring-2 ring-orange-500' : ''}`}>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    {planKey === 'premium' && <Crown className="w-5 h-5 mr-2 text-purple-600" />}
                    {planKey === 'standard' && <Star className="w-5 h-5 mr-2 text-blue-600" />}
                    {planKey === 'free' && <CheckCircle className="w-5 h-5 mr-2 text-gray-600" />}
                    {planData.planName}
                  </CardTitle>
                  <CardDescription>Commission: {planData.commission}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-sm space-y-1">
                    <p className="font-medium">{planData.tools.length} outils marketing</p>
                    {userPlan === planKey && (
                      <Badge className="bg-green-100 text-green-800">Plan actuel</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Helper function to get all available tools for a plan
function getAllAvailableTools(userPlan) {
  let tools = [];
  
  // Add free tools
  tools = [...tools, ...MARKETING_TOOLS.free.tools];
  
  // Add standard tools if applicable
  if (userPlan === 'standard' || userPlan === 'premium') {
    tools = [...tools, ...MARKETING_TOOLS.standard.tools];
  }
  
  // Add premium tools if applicable
  if (userPlan === 'premium') {
    tools = [...tools, ...MARKETING_TOOLS.premium.tools];
  }
  
  return tools;
}

export default MarketingTools;